// Mounts the `@formant/chat` runtime config for the console `/chat` surface (S4,
// DEV-16991). The package's composable surfaces (PaChatView, PaThreadList) read
// auth/token/apiBase/voice/pageCatalog from this provider via `useChatConfig()`,
// so console feeds them from its own auth + router here rather than each
// component reaching for `useAuth()` / `pageCatalog` directly.
import { fork } from '@cortex/utils/fork';
import { ChatProvider, type IChatNotice, type IMcpActivityEvent } from '@formant/chat';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useCallback, type ReactNode } from 'react';

import { useAuth } from '../../auth/AuthProvider';
import { mcpActivityBus } from '../../lib/mcp-activity-bus';
import { pageCatalog } from '../../lib/page-catalog';
import { theopolisBaseUrl } from '../../lib/theopolis';
import { useToast } from '../common/Toast';

import { formantChatAgentId } from './ThreadList';
import { useConsoleIncidentsAdapter } from './incidents-adapter';

// Same resolution as the root CortexProvider (main.tsx): hosted dev backend by
// default, overridable with VITE_API_URL for a local API.
const apiUrl = import.meta.env.VITE_API_URL ?? 'https://api.cortex-dev.formant.ai/api';

/**
 * Wraps the `/chat` surface (sidebar + conversation outlet) in the package's
 * {@link ChatProvider}. Bridges the package's internal MCP tool-activity stream
 * back onto the console's own `mcpActivityBus` (consumed by `useMcpActivity`),
 * and shares the console QueryClient so seam-rendered content (memory cards
 * fetching via `@cortex/react-query`) stays cache-coherent with the rest of the app.
 */
export function ChatHostProvider({ children }: { children: ReactNode }) {
  const { ready, token, signIn } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();

  // Surface the package's confirmation notices (e.g. "Incident added to this
  // chat") as console toasts — so both the /chat page and the floating launcher
  // confirm the same way through one seam.
  const onNotify = useCallback(
    (notice: IChatNotice) => toast.show({ kind: notice.kind, text: notice.text }),
    [toast],
  );

  const onNavigate = useCallback(
    (url: string) => {
      fork(navigate({ to: url }));
    },
    [navigate],
  );

  const onUnauthorized = useCallback(() => {
    fork(signIn());
  }, [signIn]);

  // Incidents data source — lights up the incidents rail/drawer + inline cards on
  // both chat surfaces (the package owns the UI; the console owns the data).
  const incidentsAdapter = useConsoleIncidentsAdapter(formantChatAgentId);

  const onToolCall = useCallback((e: IMcpActivityEvent) => {
    mcpActivityBus.publishCall({
      toolCallId: e.id,
      name: e.name,
      args: e.args,
      threadId: e.threadId,
    });
  }, []);

  const onToolResult = useCallback((e: IMcpActivityEvent) => {
    mcpActivityBus.publishResult({
      toolCallId: e.id,
      result: e.result,
      durationMs: e.durationMs,
      error: e.error,
    });
  }, []);

  return (
    <ChatProvider
      cortexApiBaseUrl={apiUrl}
      token={token}
      ready={ready}
      onNavigate={onNavigate}
      pageCatalog={pageCatalog}
      groupId={formantChatAgentId}
      theopolisBaseUrl={theopolisBaseUrl}
      onUnauthorized={onUnauthorized}
      onToolCall={onToolCall}
      onToolResult={onToolResult}
      queryClient={queryClient}
      incidentsAdapter={incidentsAdapter}
      onNotify={onNotify}
    >
      {children}
    </ChatProvider>
  );
}
